/**
 * Who stands at the backoffice, read off @momo/views' `session` (written by
 * the session reconciler session.sync.ts mounts) and the staff notice cell
 * notice.sync.ts writes. A session without the `mfa` claim is not admitted:
 * the rules refuse every staff read without it, so that person signs in again
 * with a code, which mints it. Staff hold no users/{uid} record, so the
 * session reads them as owing Revent's consent; that is not read here — the
 * staff notice admits them instead.
 */
import { createActor } from "@mhmo91/ican";
import { computed, state } from "@mhmo91/schmancy/state";
import type { Workspace } from "@momo/types";
import { session } from "@momo/views";
import { Licence } from "@revia/types";
import { Timestamp } from "firebase/firestore";
import { Subject } from "rxjs";

// Staff are whoever the entities' own list rule admits. Every backoffice list
// rule is data-free (STAFF_PERMISSION on Revia's organization plus mfa), so
// this probe's content is never read; it only satisfies the kernel's shape.
const listProbe: Licence.Licence = {
	v: 1,
	projectId: "list-probe",
	features: [],
	endsAt: new Date(0).toISOString(),
	graceDays: 0,
	client: "-",
	environment: "client-list-probe",
	token: "-",
	signedAt: Timestamp.fromMillis(0),
	signedBy: "-",
};

/** The staff notice, read under the signed-in staff member: unanswered,
 *  not yet acknowledged in its current version, acknowledged, or unreadable. */
export type StaffNotice = "unanswered" | "absent" | "acknowledged" | "error";
export const staffNotice = state<StaffNotice>("revia-backoffice/staff-notice").memory(
	"unanswered",
);
/** The acknowledgment in flight or refused; idle otherwise. */
export const noticeAct = state<"idle" | "busy" | "failed">(
	"revia-backoffice/staff-notice-act",
).memory("idle");
/** Acknowledge the current notice version. */
export const acknowledgeNotice$ = new Subject<void>();
/** Read the notice record again after it could not be read. */
export const noticeRetry$ = new Subject<void>();

/** Whether the signed-in person is Revia staff with a second factor, or
 *  unanswered while who is signed in, or the actor bound to them, is still
 *  being read. */
export const staffMember = computed((): "unanswered" | "sign-in" | "not-staff" | "staff" => {
	const current = session.value;
	if (current.status === "loading" || current.status === "binding") return "unanswered";
	if (current.status !== "member" || current.actor?.mfa !== true) return "sign-in";
	return createActor<Workspace.Actor>(current.actor)?.can(
		"list",
		Licence.Licence,
		listProbe,
	)
		? "staff"
		: "not-staff";
});

/** The uid the notice is read under: a staff member with a second factor. */
export const staffMemberUid = computed(() =>
	staffMember.get() === "staff" ? session.value.uid : null,
);

export type StaffGate =
	| "unanswered"
	| "sign-in"
	| "not-staff"
	| "notice-unanswered"
	| "notice"
	| "notice-error"
	| "staff";

/** The session answered, staff with a second factor, then the current notice
 *  acknowledged, then the lists. */
export const staffGate = computed((): StaffGate => {
	const member = staffMember.get();
	if (member !== "staff") return member;
	const notice = staffNotice.value;
	return notice === "acknowledged"
		? "staff"
		: notice === "absent"
			? "notice"
			: notice === "error"
				? "notice-error"
				: "notice-unanswered";
});

/** The scope every backoffice list is read under: the staff member's uid
 *  once admitted, null otherwise. */
export const staffUid = computed(() =>
	staffGate.get() === "staff" ? session.value.uid : null,
);
